import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const StoreConfigContext = createContext(null);

export const useStoreConfig = () => {
  const ctx = useContext(StoreConfigContext);
  if (!ctx) throw new Error('useStoreConfig must be inside StoreConfigProvider');
  return ctx;
};

const applyTheme = (cfg) => {
  if (!cfg) return;
  const root = document.documentElement;
  if (cfg.colors) {
    root.style.setProperty('--cs-primary', cfg.colors.primary);
    root.style.setProperty('--cs-secondary', cfg.colors.secondary);
    root.style.setProperty('--cs-accent', cfg.colors.accent);
    root.style.setProperty('--cs-background', cfg.colors.background);
    root.style.setProperty('--cs-text', cfg.colors.text);
    root.style.setProperty('--cs-cta', cfg.colors.cta_button);
    root.style.setProperty('--cs-cta-text', cfg.colors.cta_text);
  }
  if (cfg.fonts) {
    root.style.setProperty('--cs-font-heading', `'${cfg.fonts.heading}', Georgia, serif`);
    root.style.setProperty('--cs-font-body', `'${cfg.fonts.body}', system-ui, sans-serif`);
  }
};

export const StoreConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/store/config');
      setConfig(data);
      applyTheme(data);
    } catch (err) {
      console.error('Failed to load store config:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateConfig = useCallback(async (updates) => {
    const { data } = await api.put('/store/config', updates);
    setConfig(data);
    applyTheme(data);
    return data;
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  return (
    <StoreConfigContext.Provider value={{ config, loading, updateConfig, refetch: fetchConfig }}>
      {children}
    </StoreConfigContext.Provider>
  );
};
